/**
 * The workspace's two session rules (migration 1052): what happens when a
 * session's items change after someone has responded, and what deleting a cycle
 * with open sessions does. A workspace admin chooses them; everyone else reads
 * them (build contract §2.8 — a control the person may not use is not drawn).
 */
import { useState } from 'react';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  CYCLE_DELETE_OPTIONS,
  ITEM_CHANGE_OPTIONS,
  cycleDeleteRule,
  itemChangeRule,
  setWorkspaceSessionRules,
  type CycleDeleteRule,
  type ItemChangeRule,
} from '@/lib/workspaceSessionRules';

export function WorkspaceSessionRulesSection({ workspaceId }: { workspaceId: string }) {
  const workspace = useTenantStore((s) => s.workspaces.find((w) => w.id === workspaceId));
  const mayChange = useUserStore((s) => s.can('admin.settings'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const itemChange = itemChangeRule(workspace?.sessionItemMutationPolicy);
  const cycleDelete = cycleDeleteRule(workspace?.cycleDeletionPolicy);

  const save = async (rules: { itemChange?: ItemChangeRule; cycleDelete?: CycleDeleteRule }) => {
    setSaving(true);
    setError(null);
    const saved = await setWorkspaceSessionRules(workspaceId, rules);
    setSaving(false);
    if (!saved.ok) {
      setError(saved.error);
      return;
    }
    // Mirror what the server saved in memory only: updateWorkspace would write
    // the whole row back, and these columns have one write path (1052).
    useTenantStore.setState((state) => ({
      workspaces: state.workspaces.map((w) =>
        w.id === workspaceId
          ? { ...w, sessionItemMutationPolicy: saved.itemChange, cycleDeletionPolicy: saved.cycleDelete }
          : w,
      ),
    }));
  };

  const rows = [
    {
      key: 'itemChange',
      label: 'Changing a session\'s items after someone has voted',
      value: itemChange,
      options: ITEM_CHANGE_OPTIONS,
      onChange: (v: string) => save({ itemChange: v as ItemChangeRule }),
    },
    {
      key: 'cycleDelete',
      label: 'Deleting a cycle that has open sessions',
      value: cycleDelete,
      options: CYCLE_DELETE_OPTIONS,
      onChange: (v: string) => save({ cycleDelete: v as CycleDeleteRule }),
    },
  ];

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-4">
      <div>
        <p className="text-sm font-semibold text-[var(--text-pri)]">Workspace session rules</p>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          These hold for every session in this workspace, whatever its workflow.
          {!mayChange && ' A workspace admin can change them.'}
        </p>
      </div>
      {rows.map((row) => {
        const current = row.options.find((o) => o.value === row.value);
        return (
          <div key={row.key}>
            <label htmlFor={`ws-rule-${row.key}`} className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              {row.label}
            </label>
            {mayChange ? (
              <select
                id={`ws-rule-${row.key}`}
                value={row.value}
                disabled={saving}
                onChange={(e) => void row.onChange(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)]"
              >
                {row.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-[var(--text-pri)]">{current?.label}</p>
            )}
            <p className="text-xs text-[var(--text-ter)] mt-1">{current?.help}</p>
          </div>
        );
      })}
      {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
    </div>
  );
}
